import { useRef, useState } from 'react';
import { Box, Typography, Stack, LinearProgress, Alert, IconButton, Button } from '@mui/material';
import {
  CloudUploadRounded,
  CheckCircleRounded,
  ErrorRounded,
  DeleteOutlineRounded,
} from '@mui/icons-material';
import { ref as storageRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { storage, db } from '../../../lib/firebase';
import { useAuth } from '../../../contexts/auth.context';
import { ALLOWED_MIME_TYPES, MAX_FILE_SIZE_BYTES, formatFileSize } from './types';
import type { MediaImage, UploadProgress } from './types';
import { useTranslation } from 'react-i18next';

// ─── Props ────────────────────────────────────────────────────────────────────

interface ImageUploaderProps {
  tenantId: string;
  onUploaded: (image: MediaImage) => void;
  compact?: boolean;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function ImageUploader({ tenantId, onUploaded, compact = false }: ImageUploaderProps) {
  const { t } = useTranslation('sis');
  const { user } = useAuth();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [uploads, setUploads] = useState<UploadProgress[]>([]);
  const [validationError, setValidationError] = useState<string | null>(null);

  // ── File validation ────────────────────────────────────────────────────────

  function validate(file: File): string | null {
    if (!ALLOWED_MIME_TYPES.includes(file.type))
      return `Tipo no permitido: ${file.type}. Solo se aceptan JPEG, PNG, GIF, WebP y SVG.`;
    if (file.size > MAX_FILE_SIZE_BYTES)
      return t('crm.media.tooLarge', { limite: formatFileSize(MAX_FILE_SIZE_BYTES) });
    return null;
  }

  // ── Upload single file ─────────────────────────────────────────────────────

  function uploadFile(file: File) {
    const err = validate(file);
    if (err) {
      setValidationError(err);
      return;
    }
    setValidationError(null);

    const uid = crypto.randomUUID();
    const path = `tenants/${tenantId}/media/${uid}/${file.name}`;
    const sRef = storageRef(storage, path);
    const task = uploadBytesResumable(sRef, file, { contentType: file.type });

    setUploads((prev) => [...prev, { file, progress: 0, status: 'uploading' }]);

    task.on(
      'state_changed',
      (snap) => {
        const pct = Math.round((snap.bytesTransferred / snap.totalBytes) * 100);
        setUploads((prev) => prev.map((u) => (u.file === file ? { ...u, progress: pct } : u)));
      },
      (error) => {
        console.error('Upload error', error);
        setUploads((prev) =>
          prev.map((u) =>
            u.file === file ? { ...u, status: 'error', errorMsg: error.message } : u,
          ),
        );
      },
      async () => {
        try {
          const downloadUrl = await getDownloadURL(task.snapshot.ref);
          // Save metadata in Firestore
          const docRef = await addDoc(collection(db, `tenants/${tenantId}/mediaLibrary`), {
            name: file.name,
            url: downloadUrl,
            storagePath: path,
            size: file.size,
            mimeType: file.type,
            uploadedAt: serverTimestamp(),
            uploadedBy: user?.uid ?? 'unknown',
            deletedAt: null,
          });
          const image: MediaImage = {
            id: docRef.id,
            name: file.name,
            url: downloadUrl,
            storagePath: path,
            size: file.size,
            mimeType: file.type,
            uploadedAt: new Date(),
            uploadedBy: user?.uid ?? 'unknown',
          };
          setUploads((prev) =>
            prev.map((u) =>
              u.file === file ? { ...u, status: 'done', progress: 100, result: image } : u,
            ),
          );
          onUploaded(image);
        } catch (e: any) {
          setUploads((prev) =>
            prev.map((u) => (u.file === file ? { ...u, status: 'error', errorMsg: e.message } : u)),
          );
        }
      },
    );
  }

  // ── Drop handlers ──────────────────────────────────────────────────────────

  function handleFiles(files: FileList | null) {
    if (!files) return;
    Array.from(files).forEach(uploadFile);
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragging(false);
    handleFiles(e.dataTransfer.files);
  }

  function removeUpload(file: File) {
    setUploads((prev) => prev.filter((u) => u.file !== file));
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Stack spacing={1.5}>
      {/* Drop zone */}
      <Box
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        sx={{
          border: '2px dashed',
          borderColor: dragging ? 'primary.main' : 'divider',
          borderRadius: 2,
          p: compact ? 2 : 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          cursor: 'pointer',
          transition: 'all 0.2s',
          bgcolor: dragging ? 'action.hover' : 'transparent',
          '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
        }}
      >
        <CloudUploadRounded
          sx={{ fontSize: compact ? 32 : 48, color: dragging ? 'primary.main' : 'text.disabled' }}
        />
        {!compact && (
          <>
            <Typography variant="body2" fontWeight={600}>
              {t('crm.media.dropzone')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('crm.media.dropzoneHelp')}
            </Typography>
          </>
        )}
        {compact && (
          <Typography variant="caption" color="text.secondary">
            {t('crm.media.uploadOne')}
          </Typography>
        )}
      </Box>
      <input
        ref={inputRef}
        type="file"
        accept={ALLOWED_MIME_TYPES.join(',')}
        multiple
        style={{ display: 'none' }}
        onChange={(e) => handleFiles(e.target.files)}
      />

      {validationError && (
        <Alert severity="error" onClose={() => setValidationError(null)}>
          {validationError}
        </Alert>
      )}

      {/* Upload progress list */}
      {uploads.map((u) => (
        <Box
          key={u.file.name + u.file.size}
          sx={{
            border: '1px solid',
            borderColor:
              u.status === 'error'
                ? 'error.main'
                : u.status === 'done'
                  ? 'success.main'
                  : 'divider',
            borderRadius: 1.5,
            p: 1.5,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            {u.status === 'done' && <CheckCircleRounded fontSize="small" color="success" />}
            {u.status === 'error' && <ErrorRounded fontSize="small" color="error" />}
            {u.status === 'uploading' && (
              <Box sx={{ width: 20, height: 20, position: 'relative', flexShrink: 0 }}>
                <LinearProgress
                  variant="determinate"
                  value={u.progress}
                  sx={{ position: 'absolute', top: 8, left: 0, right: 0, borderRadius: 1 }}
                />
              </Box>
            )}
            <Box flex={1} minWidth={0}>
              <Typography variant="caption" fontWeight={600} noWrap display="block">
                {u.file.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatFileSize(u.file.size)}
              </Typography>
              {u.status === 'error' && (
                <Typography variant="caption" color="error.main" display="block">
                  {u.errorMsg}
                </Typography>
              )}
            </Box>
            {(u.status === 'done' || u.status === 'error') && (
              <IconButton
                size="small"
                aria-label={t('crm.media.removeFromQueue')}
                onClick={() => removeUpload(u.file)}
              >
                <DeleteOutlineRounded fontSize="small" />
              </IconButton>
            )}
          </Stack>
          {u.status === 'uploading' && (
            <LinearProgress
              variant="determinate"
              value={u.progress}
              sx={{ mt: 1, borderRadius: 1 }}
            />
          )}
        </Box>
      ))}

      {uploads.some((u) => u.status === 'done') && (
        <Button size="small" onClick={() => setUploads([])} sx={{ alignSelf: 'flex-start' }}>
          {t('crm.media.clearDone')}
        </Button>
      )}
    </Stack>
  );
}
